import { type CountryCode } from "@/config/country-config";
import { getCountryRules } from "@/config/country-rules";
import { swedishAccountDigits } from "@/features/customer-information/utils/swedish-account-fields";
import { consumeTinkVerifiedAccountNumber } from "@/features/tink-consent/verified-account-storage";

/** Payout due-date options, where the country rules offer a choice. */
export const DUE_DATE_OPTIONS = ["1", "15", "20"];

// Test-environment fallback until FLOW provides the verified Tink account.
export const FI_TEST_ACCOUNT_NUMBER = "FI 21 123456 0000078 5";

/** Where the prefilled account number came from. */
export type TrustedAccountSource = "flow" | "config" | "tink" | "none";

export interface TrustedAccountFields {
	accountNumber: string;
	clearingNumber: string;
	/**
	 * SE only: the prefilled value is a whole clearing + account number, so the
	 * form shows a single combined field and validates the total length instead
	 * of splitting at a guessed clearing boundary.
	 */
	isWholeAccountNumber: boolean;
	source: TrustedAccountSource;
}

export const EMPTY_TRUSTED_ACCOUNT_FIELDS: TrustedAccountFields = {
	accountNumber: "",
	clearingNumber: "",
	isWholeAccountNumber: false,
	source: "none",
};

/**
 * Resolves the account/clearing form defaults from the trusted account numbers
 * returned by FLOW, falling back to the FI test override or the verified Tink
 * account for the current flow. Shared by every customer-information screen so
 * the (temporary) fallback logic lives in one place.
 *
 * A Swedish number arrives whole (clearing and account together) and there is
 * no reliable way to tell where the clearing part ends, so it is kept whole and
 * the form validates the total length.
 *
 * Note: this consumes the stored Tink account for `flowId`, so it must be
 * called exactly once per mount — see `useTrustedAccountFields`.
 */
export const resolveTrustedAccountFields = (
	accountNumbers: string[] | undefined,
	flowId: string | undefined,
	country: CountryCode,
	/** `useRuntimeConfig().testEnvironment`. */
	testEnvironment = false
): TrustedAccountFields => {
	const { payoutAccount } = getCountryRules(country);
	const isSwedishAccount = payoutAccount === "swedish";
	const trustedAccountFromFlow = accountNumbers?.[0] ?? "";
	const fallbackAccountFromConfig =
		payoutAccount === "finnish-iban" && testEnvironment
			? FI_TEST_ACCOUNT_NUMBER
			: "";
	const fallbackAccountFromTink =
		!trustedAccountFromFlow && !fallbackAccountFromConfig && flowId
			? (consumeTinkVerifiedAccountNumber(flowId) ?? "")
			: "";
	const trustedAccount =
		trustedAccountFromFlow ||
		fallbackAccountFromConfig ||
		fallbackAccountFromTink;

	if (!trustedAccount) {
		return EMPTY_TRUSTED_ACCOUNT_FIELDS;
	}

	const source: TrustedAccountSource = trustedAccountFromFlow
		? "flow"
		: fallbackAccountFromConfig
			? "config"
			: "tink";
	const hasMultiple = isSwedishAccount && (accountNumbers?.length ?? 0) > 1;

	// With several accounts the customer picks one from a select, so the value
	// stays exactly as FLOW sent it.
	if (!isSwedishAccount || hasMultiple) {
		return {
			accountNumber: trustedAccount,
			clearingNumber: "",
			isWholeAccountNumber: false,
			source,
		};
	}

	return {
		accountNumber: swedishAccountDigits(trustedAccount),
		clearingNumber: "",
		isWholeAccountNumber: true,
		source,
	};
};
